import { useSearchParams } from 'next/navigation';
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { subscribeToNothing } from '../../utils/subscribeToNothing/subscribeToNothing';

const getRememberedPageData = () => window.sessionStorage.getItem('rememberedPageData');

const parseRememberedPath = (pageData: string | null): string | null => {
  if (!pageData) return null;
  try {
    return JSON.parse(pageData).path ?? null;
  } catch {
    return null;
  }
};

/**
 * Hook for managing query parameters between search/category pages and detail view.
 * Provides a single point of definition for bidirectional parameter communication.
 *
 * @returns {Object} Object containing:
 *   - getProductLinkParams: Function to prepare params for product links (TO detail view)
 *   - backLink: The computed back link URL (FROM detail view)
 */
export const useQueryParametersForDetailViewBackLink = (defaultBackLink = '/') => {
  const searchParams = useSearchParams();
  const rememberedPageData = useSyncExternalStore(
    subscribeToNothing,
    getRememberedPageData,
    () => null,
  );
  const rememberedPath = useMemo(
    () => parseRememberedPath(rememberedPageData),
    [rememberedPageData],
  );

  const navQ = searchParams?.get('nav-q');
  const backLink = rememberedPath ?? (navQ ? `/s/${navQ}` : defaultBackLink);

  /**
   * Prepares query parameters for product links (going TO detail view).
   * Adds nav-q with search term for tracking purposes.
   */
  const getProductLinkParams = useCallback(
    (
      currentQuery: Record<string, string | string[] | undefined>,
      searchQuery?: string,
    ): Record<string, string | string[] | undefined> => ({
      ...currentQuery,
      ...(searchQuery && { 'nav-q': searchQuery }),
    }),
    [],
  );

  return {
    getProductLinkParams,
    backLink,
  };
};
